import React, { useEffect, useMemo, useState } from "react";
import { generateClient } from "@aws-amplify/api";
import { Card, Row, Col, Table, Badge } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getBookings } from "@/graphql/small-queries";
import {
  BOOKED_REVENUE_CHART_COLOR,
  BOOKED_REVENUE_GRADIENT_ID,
  BOOKED_REVENUE_TITLE,
  BOOKED_REVENUE_TOOLTIP_STYLE,
  formatRevenueAxisLabel,
  formatRevenueCurrency,
  formatRevenueTooltipValue,
} from "@/components/widgets/booked-revenue-style";
import { buildBookedRevenueBookingFilters, buildBookedRevenueMetrics } from "@/utils/bookedRevenue";
import { computeDashboardRange } from "@/utils/dashboardDateRange";
import "@/components/widgets/Widgets.scss";

const client = generateClient();
const BOOKING_SORT = [{ field: "fh_booked_at", order: "desc" }];
const RANGE_OPTIONS = [
  { value: "month to date", label: "Month to date" },
  { value: "year to date", label: "Year to date" },
  { value: "last 30 days", label: "Last 30 days" },
];

function getTrendMeta(trend) {
  if (trend === "up") {
    return { symbol: "^", className: "text-success" };
  }

  if (trend === "down") {
    return { symbol: "v", className: "text-danger" };
  }

  return { symbol: "-", className: "text-secondary" };
}

function RevenueTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div style={BOOKED_REVENUE_TOOLTIP_STYLE.contentStyle}>
      <p className="mb-1 small text-muted">{label}</p>
      <p className="mb-0 fw-semibold">{formatRevenueTooltipValue(payload[0].value)}</p>
    </div>
  );
}

function ExpandedMetricSummary({ title, value, change, trend = "flat", subtitle }) {
  const trendMeta = getTrendMeta(trend);

  return (
    <div className="d-flex flex-column gap-1">
      <p className="text-muted fw-medium text-uppercase kpi-title mb-0">{title}</p>
      <div className="d-flex align-items-baseline gap-3">
        <span className="kpi-value fw-bold">{value}</span>
        {change ? (
          <span className={`d-inline-flex align-items-center gap-1 small fw-semibold ${trendMeta.className}`}>
            <span aria-hidden="true">{trendMeta.symbol}</span>
            {change}
          </span>
        ) : null}
      </div>
      <p className="text-muted small mb-0">{subtitle}</p>
    </div>
  );
}

export function BookedRevenueExpanded({
  revenueDailyData = [],
  avgBookingValue = 0,
  avgBookingDeltaText = "0.0%",
  avgBookingDeltaTrend = "flat",
  bookingsCount = 0,
  bookingsCountLabel = "Bookings in Range",
  bookingsCountDeltaText = "+0",
  bookingsCountDeltaTrend = "flat",
  topEventType = "N/A",
  topEventAmount = 0,
  bookingsTableRows = [],
}) {
  const avgDeltaClass =
    avgBookingDeltaTrend === "up"
      ? "small text-success mb-0"
      : avgBookingDeltaTrend === "down"
        ? "small text-danger mb-0"
        : "small text-muted mb-0";
  const bookingsDeltaClass =
    bookingsCountDeltaTrend === "up"
      ? "small text-success mb-0"
      : bookingsCountDeltaTrend === "down"
        ? "small text-danger mb-0"
        : "small text-muted mb-0";

  return (
    <div className="d-flex flex-column gap-4 ps-4 pe-4">
      <Card className="border-0 shadow-sm">
        <Card.Body>
          <div className="expanded-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueDailyData} margin={{ top: 8, right: 8, bottom: 18, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke="#6c757d"
                  fontSize={12}
                  padding={{ left: 0, right: 0 }}
                  minTickGap={24}
                />
                <YAxis
                  stroke="#6c757d"
                  fontSize={12}
                  tickFormatter={formatRevenueAxisLabel}
                  width={80}
                />
                <Tooltip content={<RevenueTooltip />} cursor={{ stroke: "#cbd5e1", strokeDasharray: "4 4" }} />
                <defs>
                  <linearGradient id={BOOKED_REVENUE_GRADIENT_ID} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={BOOKED_REVENUE_CHART_COLOR} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={BOOKED_REVENUE_CHART_COLOR} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={BOOKED_REVENUE_CHART_COLOR}
                  strokeWidth={2}
                  fill={`url(#${BOOKED_REVENUE_GRADIENT_ID})`}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card.Body>
      </Card>

      <Row className="g-3">
        <Col md={4}>
          <Card className="summary-stat-card border-0 shadow-sm">
            <Card.Body>
              <p className="text-muted small mb-1">Avg. Booking Value</p>
              <p className="h4 fw-bold mb-1">{formatRevenueCurrency(avgBookingValue)}</p>
              <p className={avgDeltaClass}>{avgBookingDeltaText}</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="summary-stat-card border-0 shadow-sm">
            <Card.Body>
              <p className="text-muted small mb-1">{bookingsCountLabel}</p>
              <p className="h4 fw-bold mb-1">{bookingsCount}</p>
              <p className={bookingsDeltaClass}>{bookingsCountDeltaText}</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="summary-stat-card border-0 shadow-sm">
            <Card.Body>
              <p className="text-muted small mb-1">Top Event Type</p>
              <p className="h4 fw-bold mb-1">{topEventType}</p>
              <p className="small text-muted mb-0">{formatRevenueCurrency(topEventAmount)}</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Client</th>
                <th>Date</th>
                <th>Time</th>
                <th>Event</th>
                <th className="text-end">Amount</th>
              </tr>
            </thead>
            <tbody>
              {bookingsTableRows.length ? (
                bookingsTableRows.map((booking) => (
                  <tr key={booking.id}>
                    <td className="text-nowrap">{booking.id}</td>
                    <td className="fw-semibold">{booking.client}</td>
                    <td>{booking.date}</td>
                    <td>{booking.time}</td>
                    <td>
                      <Badge bg="secondary-subtle" text="dark" className="fw-normal">
                        {booking.event}
                      </Badge>
                    </td>
                    <td className="text-end fw-semibold">{formatRevenueCurrency(booking.amount)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No bookings found for this time range.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
}

function BookedRevenueExpandedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(location.state?.defaultPeriod || "year to date");
  const range = useMemo(() => computeDashboardRange(period), [period]);
  const bookingFilters = useMemo(
    () => buildBookedRevenueBookingFilters(range.start, range.end, period),
    [period, range.end, range.start],
  );

  useEffect(() => {
    let isMounted = true;

    setLoading(true);
    client
      .graphql({
        query: getBookings,
        variables: { filters: bookingFilters, sorting: BOOKING_SORT },
      })
      .then((response) => {
        if (!isMounted) return;
        setBookings(response?.data?.getBookings?.bookings || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error loading bookings:", error);
        if (isMounted) {
          setBookings([]);
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [bookingFilters]);

  const metrics = useMemo(
    () => buildBookedRevenueMetrics(bookings, { startDate: range.start, endDate: range.end, period }),
    [bookings, period, range.end, range.start],
  );

  return (
    <div className="dashboard-theme-root">
      <main className="dashboard-shell">
        <div className="dashboard-max py-4 py-md-5">
          <div className="d-flex flex-column gap-4 ps-4 pe-4">
            <div className="d-flex justify-content-between align-items-end gap-3 ps-4 pe-4 flex-wrap">
              <ExpandedMetricSummary
                title={BOOKED_REVENUE_TITLE}
                value={metrics.value}
                change={metrics.change}
                trend={metrics.trend}
                subtitle={metrics.subtitle}
              />

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <select
                  className="form-select form-select-sm w-auto"
                  value={period}
                  onChange={(event) => setPeriod(event.target.value)}
                  aria-label="Revenue time range"
                >
                  {RANGE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  className="btn btn-sm"
                  onClick={() => navigate("/app/dashboard")}
                  style={{
                    backgroundColor: "#2E2868",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    padding: "6px 12px",
                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                  }}
                  aria-label="Close"
                  title="Close"
                >
                  x Close
                </button>
              </div>
            </div>

            {loading ? (
              <Card className="border-0 shadow-sm">
                <Card.Body className="text-center text-muted py-5">Loading...</Card.Body>
              </Card>
            ) : (
              <BookedRevenueExpanded
                revenueDailyData={metrics.revenueDailyData}
                avgBookingValue={metrics.avgBookingValue}
                avgBookingDeltaText={metrics.avgBookingDeltaText}
                avgBookingDeltaTrend={metrics.avgBookingDeltaTrend}
                bookingsCount={metrics.bookingsCount}
                bookingsCountLabel={metrics.bookingsCountLabel}
                bookingsCountDeltaText={metrics.bookingsCountDeltaText}
                bookingsCountDeltaTrend={metrics.bookingsCountDeltaTrend}
                topEventType={metrics.topEventType}
                topEventAmount={metrics.topEventAmount}
                bookingsTableRows={metrics.bookingsTableRows}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default BookedRevenueExpandedPage;
